export * from './actions';
export * from './dates';
export * from './oauth';

const landingRoutes = {
  home: '/',
  signUpSuccess: '/?notice=signup',
  features: '/features',
  candidates: '/candidates'
};

const authRoutes = {
  auth: '/auth',
  signIn: '/auth/sign_in',
  signUpCandidate: '/auth/sign_up',
  signUpEmployer: '/auth/sign_up/company',
  forgotPassword: '/auth/password/new',
  resetPassword: '/auth/password/reset'
};

const profileRoutes = {
  base: '/profile',
  general: {
    base: '/profile/general',
    edit: {
      base: '/profile/general/edit',
      employment: '/profile/general/edit#employment',
      education: '/profile/general/edit#education'
    }
  },
  preferences: '/profile/preferences',
  workPlaces: '/profile/workplaces',
  invitations: '/profile/invitations',
  applications: '/profile/applications',
  roles: '/profile/roles',
  settings: '/profile/settings'
};

const freelancersRoutes = {
  base: '/recruiters',
  candidatesSearch: '/candidate_search',
  candidatesSearchApplicant: '/candidate_search/:applicantId',
  dashboard: '/recruiters/dashboard',
  talentPool: {
    base: '/talent-pool',
    applicant: '/talent-pool/:applicantId'
  },
  roles: '/recruiters/roles',
  jobs: {
    base: '/recruiters/jobs',
    get job() {
      return {
        job: this.base + '/:jobId',
        get applicant() {
          return this.job + '/applicants/:applicantId';
        }
      };
    }
  },
  clients: '/recruiters/clients',
  get settings() {
    const base = '/recruiters/settings';
    return {
      get general() {
        return base + '/general';
      },
      get preferences() {
        return base + '/preferences';
      },
      get security() {
        return base + '/security';
      }
    };
  }
};

const employer = {
  dashboard: '/dashboard',
  candidatesSearch: '/candidate_search',
  candidatesSearchApplicant: '/candidate_search/:applicantId',
  talentPool: {
    base: '/talent-pool',
    applicant: '/talent-pool/:applicantId'
  },
  companyProfile: {
    base: '/edit',
    general: '/edit/general',
    description: '/edit/description',
    keyPerson: '/edit/key-person',
    team: '/edit/team',
    freetext: '/edit/free-text',
    media: '/edit/media',
    locations: '/edit/locations',
    settings: '/edit/settings',
    activity: {
      base: '/edit/activities/:activityId',
      new: '/edit/activities/new'
    }
  },
  preferences: {
    hiring: '/preferences/hiring_teams',
    career: '/preferences/career_page',
    partners: '/preferences/partners',
    emailTemplates: '/preferences/email_templates',
    emailTemplatesNew: '/preferences/email_templates/new',
    emailTemplatesEdit: '/preferences/email_templates/:templateId/edit',
    tags: '/preferences/tags'
  },
  careers: '/careers',
  documents: '/docs',
  jobs: {
    base: '/jobs',
    job: '/jobs/:jobId',
    new: '/jobs/new',
    edit: '/jobs/:jobId/edit',
    clone: '/jobs/:jobId/clone',
    application: '/jobs/:jobId/application_forms/edit',
    suggestions: '/jobs/:jobId/suggestions',
    advertising: '/jobs/:jobId/advertising',
    feeds: '/jobs/:jobId/feeds',
    members: '/jobs/:jobId/members',
    applicants: {
      base: '/jobs/:jobId/applicants',
      applicant: '/jobs/:jobId/applicants/:applicantId'
    }
  },
  inactive: '/inactive_error'
};

const adminRoutes = {
  base: '/admin',
  user: `/admin/users/:id`,
  notifications: '/admin/notifications',
  job_board_notifications: '/admin/job_board_notifications',
  return_to: '/admin/return_to'
};

const publicRoutes = {
  resources: '/resources',
  magazine: '/magazine'
};

const jobsRoutes = {
  base: '/jobs',
  job: '/jobs/:jobId',
  applications: {
    base: '/jobs/:jobId/applications',
    new: '/jobs/:jobId/applications/new',
    application: '/jobs/:jobId/applications/:applicationId'
  }
};

const universitiesRoutes = {
  base: '/universities',
  university: '/universities/:universityId',
  cv_books: {
    base: '/universities/:universityId/cv_books',
    edit: '/universities/:universityId/cv_books/edit',
    cv_book: '/universities/:universityId/cv_books/:cv_bookId',
    students: {
      base: '/universities/:universityId/cv_books/:cv_bookId/students',
      student: '/universities/:universityId/cv_books/:cv_bookId/students/:applicantId'
    }
  }
};

const candidatesRoutes = {
  base: '/candidates',
  get profile() {
    return {
      base: this.base + '/profile',
      get edit() {
        return {
          base: this.base + '/edit',
          get general() {
            return this.base + '/general';
          },
          get resume() {
            return this.base + '/resume';
          },
          get employment() {
            return this.base + '/employment';
          },
          get education() {
            return this.base + '/education';
          }
        };
      }
    };
  },
  get roles() {
    return this.base + '/roles';
  },
  get applications() {
    return this.base + '/applications';
  },
  get preferences() {
    return {
      base: this.base + '/preferences',
      get roles() {
        return this.base + '/roles';
      },
      get workPlaces() {
        return this.base + '/work-places';
      }
    };
  },
  get magazine() {
    return this.base + '/magazine';
  },
  get learning() {
    return this.base + '/learning';
  },
  get settings() {
    return this.base + '/settings';
  }
};

export const ROUTES = {
  defaultPublicRoute: '/auth/sign_in',
  defaultFreelancerPrivateRoute: freelancersRoutes.dashboard,
  defaultEmployerPrivateRoute: employer.dashboard,
  ...landingRoutes,
  ...publicRoutes,
  ...authRoutes,
  profile: profileRoutes,
  employer,
  candidatesRoutes,
  freelancers: freelancersRoutes,
  jobs: jobsRoutes,
  universities: universitiesRoutes,
  admin: adminRoutes
};

export const KEY_CODES = {
  ENTER: 13,
  ESC: 27,
  RIGHT_ARROW: 39,
  LEFT_ARROW: 37
};

export const APPLICATION_STEPS = {
  Pending: 'Pending',
  Suggested: 'Suggested',
  Applied: 'Applied',
  Shortlist: 'Shortlist',
  Hired: 'Hired'
};

export const SOURCE_TYPES = {
  not_specified: 0,
  suggested: 1,
  manual_suggested: 2,
  freelancers: 3
};

export const STEPS_KINDS = {
  default: 0,
  custom: 1,
  extended: 3
};

export const APPLICANTS_VIEW_MODS = {
  APPLICANTS: 'APPLICANTS',
  INTERESTS: 'INTERESTS',
  STUDENTS: 'STUDENTS',
  CANDIDATES_SEARCH: 'CANDIDATES_SEARCH'
};

// Order of this array will imply order of sorting by status in talent pool
// see also app/javascript/react-spa/src/features/employer/applicantsTable/constants/States.js  comment
export const STATES = [
  { name: 'new', title: 'Review' },
  { name: 'passed', title: 'Passed' },
  { name: 'applied', title: 'Applied' },
  { name: 'invited', title: 'Invited' },
  { name: 'hold', title: 'Hold' },
  { name: 'interested', title: 'Interested' },
  { name: 'rejected', title: 'Rejected' },
  { name: 'rejected_and_emailed', title: 'Rejected+Emailed' },
  { name: 'withdrawn', title: 'Withdrawn' },
  { name: 'sainoo', title: 'Sainoo' },
  { name: 'deleted', title: 'Deleted' },
  { name: 'pending', title: 'Pending' },
  { name: 'invited_by_freelancer', title: 'Invited by freelancer' },
  { name: 'sending_email', title: 'Sending Email' }
].map((item, i) => ({ ...item, id: i + 1 }));

export const FREELANCER_STATES = [
  { id: 1, name: 'active', title: 'In process' },
  { id: 2, name: 'passive', title: 'Passive' }
];

export const STATES_MAP = {
  new: 'new',
  passed: 'passed',
  hold: 'hold',
  rejected: 'rejected',
  rejected_and_emailed: 'rejected_and_emailed',
  withdrawn: 'withdrawn',
  sainoo: 'sainoo',
  deleted: 'deleted',
  pending: 'pending',
  invited_by_freelancer: 'invited_by_freelancer',
  sending_email: 'sending_email'
};

export const getApplicantStatesColors = (state, stepName = '') =>
  ({
    new: {
      bg: 'statusBadge.yellow.bg',
      color: 'statusBadge.yellow.color',
      children: 'Review' + (stepName ? ` - ${stepName}` : '')
    },
    passed: { bg: 'statusBadge.green.bg', color: 'statusBadge.green.color', children: 'Passed' },
    hold: { bg: 'statusBadge.grey.bg', color: 'statusBadge.grey.color', children: 'Hold' },
    rejected: {
      bg: 'statusBadge.red.bg',
      color: 'statusBadge.red.color',
      children: 'Rejected' + (stepName ? ` - ${stepName}` : '')
    },
    rejected_and_emailed: {
      bg: 'statusBadge.red.bg',
      color: 'statusBadge.red.color',
      children: 'Rejected+Emailed' + (stepName ? ` - ${stepName}` : '')
    },
    cold_lead: {
      bg: 'iceBlueTwo',
      color: 'textSecondary',
      children: 'Lead' + (stepName ? ` - ${stepName}` : '')
    },
    interested: { bg: 'statusBadge.blue.bg', color: 'statusBadge.blue.color', children: 'Interested' },
    recruited: { bg: 'statusBadge.blue.bg', color: 'statusBadge.blue.color', children: 'Introduced' },
    withdrawn: { bg: 'statusBadge.red.bg', color: 'statusBadge.red.color', children: 'Withdrawn' },
    sainoo: { bg: 'statusBadge.sainoo.bg', color: 'statusBadge.sainoo.color', children: 'Sainoo' },
    deleted: { bg: 'statusBadge.darkGrey.bg', color: 'statusBadge.darkGrey.color', children: 'Deleted' },
    pending: { bg: 'iceBlueTwo', color: 'textSecondary', children: 'Pending' },
    invited_by_freelancer: { bg: 'secondary', children: 'Invited by freelancer' },
    active: { bg: 'statusBadge.yellow.bg', color: 'statusBadge.yellow.color', children: 'In process' },
    passive: { bg: 'lightPurple', color: 'boldPurple', children: 'Passive' },
    applied: { bg: 'statusBadge.yellow.color', color: 'statusBadge.yellow.bg', children: 'Applied' },
    sending_email: { bg: 'iceBlueTwo', color: 'textSecondary', children: 'Sending Email' }
  }[state]);

export const STATUSES = {
  review: { id: 1, name: 'new', title: 'Review' },
  passed: { id: 2, name: 'passed', title: 'Passed' },
  hold: { id: 3, name: 'hold', title: 'Hold' },
  rejected: { id: 4, name: 'rejected', title: 'Rejected' },
  rejected_and_emailed: { id: 5, name: 'rejected_and_emailed', title: 'Rejected+Emailed' },
  interested: { id: 6, name: 'interested', title: 'Interested' },
  sainoo: { id: 7, name: 'sainoo', title: 'Sainoo' },
  recruited: { id: 8, name: 'recruited', title: 'Introduced' },
  sending_email: { id: 8, name: 'sending_email', title: 'Sending Email' }
};

export const STATUSES_MAP = {
  new: 'to_new',
  rejected: 'to_rejected',
  rejected_and_emailed: 'to_rejected_and_emailed',
  hold: 'to_hold',
  passed: 'to_passed',
  sainoo: 'to_sainoo'
};

export const APP_USER_PERMISSIONS = {
  ADMIN: 'admin',
  RECRUITMENT_MANAGER: 'recruitment_manager',
  RECRUITER: 'recruiter'
};

export const USER_ROLES = {
  candidate: 'candidate',
  employer: 'employer',
  university: 'university',
  freelancer: 'freelancer'
};

export const AVAILABLE_FOR_WORK_STATUS = {
  actively_looking: 'actively_looking',
  open_to_opportunities: 'open_to_opportunities',
  not_looking: 'not_looking'
};

export const SOURCES = {
  INTERESTS: 1,
  APPLICANTS: 2,
  COMMUNITY: 3,
  RECRUITERS: 4
};

export const FREELANCER_VIEW_TYPES = {
  ROLES: 'ROLES',
  JOBS: 'JOBS',
  DASHBOARD: 'DASHBOARD'
};

export const currencies = ['GBP', 'EUR', 'USD'];

export const salaries = ['Unpaid', '< 35K', '35 - 49K', '50 - 69K', '70 - 89K', '90 - 120K', '120 - 150K', '> 150K'];

export const experiences = [
  'Internship',
  'Graduate',
  'Entry-level (0 to 3 years)',
  'Associate (3 to 6 years)',
  'Mid-Senior level (6 to 9 years)',
  'Director (9 to 12 years)',
  'Executive (12+ years)'
];

export const JOB_STATES_MAP = {
  published: 'published',
  unpublished: 'unpublished',
  draft: 'draft',
  closed: 'closed'
};

export const EXPERIENCES_MAP = {
  Internship: { from: 0, to: 0 },
  Graduate: { from: 0, to: 0 },
  'Entry-level (0 to 3 years)': { from: 0, to: 3 },
  'Associate (3 to 6 years)': { from: 3, to: 6 },
  'Mid-Senior level (6 to 9 years)': { from: 6, to: 9 },
  'Director (9 to 12 years)': { from: 9, to: 12 },
  'Executive (12+ years)': { from: 12, to: 12 }
};

export const EXPERIENCES_INTERVALS_ORDERS = {
  '< 1 year': 0,
  '1 - 3 years': 1,
  '3 - 6 years': 2,
  '6 - 9 years': 3,
  '9 - 12 years': 4,
  '> 12 years': 5
};

export const sponsorVisasOptions = ['yes', 'no', 'maybe'];

export const themeFieldNameToLevelMap = {
  main_themes: 'main_theme',
  sub_themes: 'sub_theme',
  skills_families: 'skills_family',
  focuses: 'focus'
} as const;

export const workPlaceLevels = {
  union: 'visa_framework',
  country: 'country',
  city: 'city'
};

export const getVisaStatuses = (place: string) => [
  {
    label: `I am authorized to work in ${place} and I don’t need a visa sponsorship`,
    value: 'authorized_and_have_visa'
  },
  {
    label: `I am currently authorized to work in ${place} but I will need a visa sponsorship/ transfer`,
    value: 'authorized_and_need_visa'
  },
  {
    label: `I am not authorized to work in ${place} and I will need a visa sponsorship`,
    value: 'not_authorized_and_need_visa'
  }
];

export const socials = {
  facebook: 'https://www.facebook.com/sainoofficial',
  linkedin: 'https://www.linkedin.com/company/sainoo',
  email: 'contact@sainoo.com'
};

export const EMPLOYEES_COUNT = [
  '2-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1,000',
  '1,001-5,000',
  '5,001-10,000',
  '10,000'
];

export const ignoreThoseErrorMessages = [
  '401 Unauthorized',
  'ActiveRecord::RecordNotFound',
  { str: 'is missing', pathname: ROUTES.employer.jobs.new }
];
